import { useRetrieveAddressesByCustomerQuery } from "@/react/services/common/addressApi";
import { formatAddress } from "@/react/features/smp/address-management/schema";
import { ValidationWrapper } from "@/react/components/table/components";
import Select from "@8x8/oxygen-select";
import React from "react";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";

/**
 *
 * @param {Object<string, Address>} data
 * @returns {Array<{label: string, value: string}>}
 */
const mapToOptions = (data) =>
  Object.values(data || {}).map((address) => ({
    value: address.id,
    label: formatAddress(address),
  }));

/**
 * Renders an async drop-down address selector, used for table cells and column setters
 *
 * @param {string} addressId
 * @param {string} address
 * @param {function(string): void} onChangeAddressId
 * @param {function(string): void} onChangeAddress
 * @param {function(): void} onBlur
 * @param {Array<string>} errors
 * @returns {JSX.Element}
 * @constructor
 */
export const AddressSelector = ({
  addressId,
  address,
  onChangeAddressId,
  onChangeAddress,
  onBlur,
  errors,
}) => {
  /** @type {{data: Object<string, Address>, isFetching: boolean}} */
  const query = useRetrieveAddressesByCustomerQuery();
  const { data, isFetching } = query;

  const options = mapToOptions(data);

  const selectValue = options.find((opt) => opt.value === addressId) || {
    value: addressId,
    label: address,
  };

  const onChange = (opt) => {
    onChangeAddressId(opt?.value);
    const newAddress = data[opt?.value];
    onChangeAddress(formatAddress(newAddress));
  };

  return (
    <FullWidthDiv>
      <ValidationWrapper errors={errors}>
        <Select
          size={"small"}
          testId={"address-select"}
          isLoading={isFetching}
          options={options}
          value={selectValue}
          onChange={onChange}
          onBlur={onBlur}
          hasError={errors?.length > 0}
          isClearable={true}
          isSearchable={true}
        />
      </ValidationWrapper>
    </FullWidthDiv>
  );
};
